// UserProfileCreations — AI-generated content grid
import { Code2, Image, Music, Share2, Video } from 'lucide-react';
import { useEffect, useState } from 'react';
import { API_URL, apiFetch } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

interface Creation {
  id?: number; type: 'image' | 'music' | 'video' | 'code'; url?: string; title: string; created_at: string
}

interface Props { userId: number; isOwn: boolean }

const TYPE_META: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  image: { icon: <Image size={11} />, color: '#6366f1', label: 'Image' },
  music: { icon: <Music size={11} />, color: '#ec4899', label: 'Music' },
  video: { icon: <Video size={11} />, color: '#f59e0b', label: 'Video' },
  code:  { icon: <Code2 size={11} />, color: '#10b981', label: 'Code' },
}

export default function UserProfileCreations({ isOwn }: Props) {
  const { isAuthenticated } = useAuth()
  const [items, setItems] = useState<Creation[]>([])
  const [loading, setLoading] = useState(true)
  const [sharing, setSharing] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    apiFetch(`${API_URL}/api/aicloud/skill-runs?limit=40`)
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => {
        const runs: any[] = d?.runs ?? []
        const mapped: Creation[] = runs
          .filter((r: any) => r.result)
          .map((r: any) => {
            const name: string = r.skill_name ?? ''
            let type: Creation['type'] = 'image'
            if (name.includes('music') || name.includes('audio')) type = 'music'
            else if (name.includes('video')) type = 'video'
            else if (name.includes('code') || name.includes('script')) type = 'code'
            return { id: r.id, type, url: r.result, title: r.skill_name ?? 'Creation', created_at: r.created_at }
          })
        setItems(mapped)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  const shareToSocial = async (item: Creation) => {
    if (!isAuthenticated) return
    setSharing(item.title + item.created_at)
    await apiFetch(`${API_URL}/api/social/posts`, {
      method: 'POST',
      body: JSON.stringify({ content: item.title, media_url: item.url, post_type: item.type })
    }).catch(() => {})
    setSharing(null)
  }

  const shareToProfile = (item: Creation) => {
    window.dispatchEvent(new CustomEvent('runai:share-to-profile', {
      detail: { type: item.type, url: item.url, title: item.title }
    }))
  }

  const shareToBuzz = (item: Creation) => {
    window.dispatchEvent(new CustomEvent('runai:open-buzz-share', {
      detail: { type: item.type, url: item.url, title: item.title }
    }))
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>Loading creations...</div>

  if (items.length === 0) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <Image size={32} style={{ color: 'var(--text-tertiary)', marginBottom: 10 }} />
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>No creations yet</div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>AI-generated images, music, videos and code will appear here</div>
    </div>
  )

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
      {items.map((item, i) => {
        const meta = TYPE_META[item.type] ?? TYPE_META.image
        const key = `${item.id ?? i}`
        const isSharing = sharing === item.title + item.created_at
        return (
          <div key={key} style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-color)', background: 'var(--body-bg)', display: 'flex', flexDirection: 'column' }}>
            {/* Thumbnail */}
            <div style={{ position: 'relative', aspectRatio: '1', background: `${meta.color}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {item.type === 'image' && item.url
                ? <img src={item.url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : item.type === 'video' && item.url
                  ? <video src={item.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                  : <div style={{ color: meta.color, opacity: 0.4 }}>{item.type === 'music' ? <Music size={36} /> : item.type === 'code' ? <Code2 size={36} /> : <Image size={36} />}</div>
              }
              {/* Type badge */}
              <div style={{ position: 'absolute', top: 6, left: 6, display: 'flex', alignItems: 'center', gap: 3, padding: '3px 7px', borderRadius: 5, background: `${meta.color}cc`, color: '#fff', fontSize: 10, fontWeight: 700, backdropFilter: 'blur(4px)' }}>
                {meta.icon}{meta.label}
              </div>
            </div>

            {/* Info + actions */}
            <div style={{ padding: '8px 10px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</div>
              {isOwn && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <ShareBtn label="Social" onClick={() => shareToSocial(item)} loading={isSharing} color="#6366f1" />
                  <ShareBtn label="Profile" onClick={() => shareToProfile(item)} color="#10b981" />
                  <ShareBtn label="Buzz" onClick={() => shareToBuzz(item)} color="#f59e0b" />
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function ShareBtn({ label, onClick, loading, color }: { label: string; onClick: () => void; loading?: boolean; color: string }) {
  return (
    <button onClick={onClick} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 5, border: `1px solid ${color}44`, background: `${color}12`, color, fontSize: 10, fontWeight: 600, cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.6 : 1, transition: 'all 0.15s' }}>
      <Share2 size={9} />{label}
    </button>
  )
}
